import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Toolbar from "../components/Views/Toolbar";
import LeftPanel from "../components/Views/LeftPanel";
import FloorPlan from "../components/Views/FloorPlan";
import { userStoreContext } from "../store/userStore";
import { FloorplannerStoreContext } from "../store/floorplannerStore";
import { ApolloClient } from "@apollo/client";
import PropertyPanel from "../components/Views/PropertyPanel";
import { editorStoreContext } from '../store/editorStore';
import ZoomControls from "../components/FloorPlan/ZoomControls";
import AddToPresentationCta from "../components/FloorPlan/AddToPresentationCta";
import { renderStore } from "../store/renderStore";
import { DownloadProject } from "../components/Views/DownloadProject";
import { AddToPresentation } from "../components/Views/AddToPresentation";
import { Render3DViewModal } from "../components/Views/Render3DViewModal";
import PaywallModal from "../components/Views/PaywallModal";
import Clarity from '@microsoft/clarity';
import HistoryControls from "../components/FloorPlan/HistoryControls";
import { RiCloseLargeLine } from "react-icons/ri";
import NotificationBar from "../components/Views/NotificationBar";
import WallConstructionInfoPanel from "../components/Views/WallConstructionInfoPanel";
import LineConstructionInfoPanel from "../components/Views/LineConstructionInfoPanel";
import HiddenWallsInfoPanel from "../components/Views/HiddenWallsInfoPanel";
import CameraViewControls from "../components/FloorPlan/CameraViewControls";
import { isFloorplannerDebugLoggingEnabled } from "../utils/debugLogging";
import FloorplanCollabBanner from "../components/Views/FloorplanCollabBanner";
import { useFloorplanCollab } from "../hooks/useFloorplanCollab";
import { floorplannerStore } from "../store/floorplannerStore";
import { useTranslation } from "react-i18next";
import { RoomPickerWizard } from "../features/roomPicker/RoomPickerWizard";
import {
  clearRoomPickerFlag,
  shouldOpenRoomPicker,
} from "../features/roomPicker/roomPickerSession";

const Main: React.FC<{
  client: ApolloClient<any>;
}> = observer(({ client }) => {
  const { t } = useTranslation();
  const initializedRef = useRef(false);
  const userStore = useContext(userStoreContext);
  const editorStore = useContext(editorStoreContext);
  const floorplanStore = useContext(FloorplannerStoreContext);
  const { id } = useParams();
  const downloadProjectRef = useRef<HTMLDivElement>(null);
  const addToPresentationRef = useRef<HTMLDivElement>(null);
  const render3DViewModalRef = useRef<HTMLDivElement>(null);
  const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  // State to manage visibility
  const [isVisible, setIsVisible] = useState(true);

  const collabEnabled = Boolean(
    floorplannerStore.id && floorplannerStore.isShared,
  );
  const { takeOver } = useFloorplanCollab(collabEnabled);

  // Open the room-picker wizard from ?roomPicker=1 once an empty project is ready.
  useEffect(() => {
    if (!shouldOpenRoomPicker()) return;
    if (!floorplanStore.initialized || floorplanStore.isInitializing) return;

    const isEmpty =
      floorplanStore.wallsMap.size === 0 &&
      floorplanStore.symbolsMap.size === 0 &&
      floorplanStore.singleLinesMap.size === 0;

    if (isEmpty) {
      editorStore.openRoomPicker();
    } else {
      clearRoomPickerFlag();
    }
  }, [
    editorStore,
    floorplanStore.initialized,
    floorplanStore.isInitializing,
    floorplanStore.wallsMap.size,
    floorplanStore.symbolsMap.size,
    floorplanStore.singleLinesMap.size,
  ]);

  // Function to handle close action
  const handleClose = () => {
    setIsVisible(false);
  };
  // Initialize Clarity project
  useEffect(() => {
    if (clarityProjectId) {
      Clarity.init(clarityProjectId);
    }
  }, [clarityProjectId]);

  useEffect(() => {
    if (!initializedRef.current) {
      try {
        userStore.initialize(client);
        editorStore.initialize(client);
        floorplanStore.initialize(client, id);
        renderStore.initialize(client);

        // Expose stores globally for testing in non-production environment
        if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
          (window as any).editorStore = editorStore;
          (window as any).floorplannerStore = floorplanStore;
          (window as any).renderStore = renderStore;
          if (isFloorplannerDebugLoggingEnabled()) {
            console.log('Stores exposed globally for testing');
          }
        }
      } catch (error) {
        console.error("Failed to initialize stores", error);
      }
      initializedRef.current = true;
    }
  }, [client, floorplanStore, userStore, editorStore]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // If click target is outside any of the popups, close them
      if (
        downloadProjectRef.current &&
        !downloadProjectRef.current.contains(event.target as Node)
      ) {
        editorStore.closeDownloadProjectPopup();
      }
      if (
        addToPresentationRef.current &&
        !addToPresentationRef.current.contains(event.target as Node)
      ) {
        editorStore.closeAddToPresentationPopup();
      }
      if (
        !editorStore.render3DViewGenerating &&
        render3DViewModalRef.current &&
        !render3DViewModalRef.current.contains(event.target as Node)
      ) {
        editorStore.closeRender3DViewModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [downloadProjectRef, addToPresentationRef, render3DViewModalRef, editorStore]);

  // Dev-only: Keyboard shortcut to toggle debug mode
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+D to toggle debug mode
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        if (editorStore.isDebugModeActive) {
          editorStore.disableAllDebugVisualizations();
        } else {
          editorStore.enableAllDebugVisualizations();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editorStore]);

  return (
    <div className="w-screen h-screen flex flex-col">

      {/* Main Content */}
      <div
        className="flex flex-grow overflow-hidden"
      >

        {/* Floor Plan */}
        <div className="flex-grow bg-[#EAEAEA] overflow-hidden relative">
          <FloorPlan />

          {/* Loading indicator - slim animated bar at top of canvas */}
          {floorplanStore.isInitializing && (
            <div className="absolute top-0 left-0 right-0 z-50 h-1.5 bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-blue-500 animate-pulse"
                style={{
                  width: '60%',
                  animation: 'loading-slide 1.5s ease-in-out infinite'
                }}
              />
              <style>{`
                @keyframes loading-slide {
                  0% { transform: translateX(-100%); }
                  50% { transform: translateX(150%); }
                  100% { transform: translateX(-100%); }
                }
              `}</style>
            </div>
          )}

          {/* Canvas interaction blocker - transparent overlay that blocks pointer events */}
          {floorplanStore.isInitializing && (
            <div
              className="absolute inset-0 z-40 cursor-wait flex items-center justify-center"
            >
              <div className="bg-white/90 backdrop-blur-sm px-8 py-5 rounded-lg shadow-lg border border-gray-200 min-w-[320px]">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                    <span className="text-gray-700 font-medium">{t("routes.loadingFloorplan")}</span>
                  </div>
                  <span className="text-gray-500 text-sm min-h-[20px]">
                    {floorplanStore.loadingStepDescription && (
                      <>{floorplanStore.loadingStepDescription} ({floorplanStore.loadingStep}/{floorplanStore.loadingTotalSteps})</>
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Paste overlay - blocks interaction until pasted wall geometry settles */}
          {!floorplanStore.isInitializing && editorStore.isPasting && (
            <div
              className="absolute inset-0 z-40 cursor-wait flex items-center justify-center"
            >
              <div className="bg-white/90 backdrop-blur-sm px-8 py-5 rounded-lg shadow-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
                  <span className="text-gray-700 font-medium">{t("routes.pastingFloorplan")}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notification Bar */}
        <NotificationBar isVisible={isVisible} onClose={handleClose} />

        {/* Toolbar, make it a floatable bar wide as necessary, background of #FCFCFC and rounded corners
        with a frame and a drop shadow fixed on top 3 margin but centered in window */}
        <div
          className={`absolute ${isVisible ? 'top-6' : 'top-3'
            } left-1/2 transform -translate-x-1/2 bg-toolbkg rounded-md border border-gray-300 shadow-md`}
        >  <Toolbar />
        </div>

        {/* Wall Construction Mode Info Panel - positioned below toolbar */}
        <WallConstructionInfoPanel isVisible={isVisible} />
        
        {/* Line Construction Mode Info Panel (Elevation) - positioned below toolbar */}
        <LineConstructionInfoPanel isVisible={isVisible} />

        {/* Temporary 3D wall hide recovery banner */}
        <HiddenWallsInfoPanel isVisible={isVisible} />

        <FloorplanCollabBanner onTakeOver={takeOver} />

        {/* Debug Mode Indicator - positioned below toolbar (activated via ?debug=1 URL param) */}
        {editorStore.debugPanelVisible && (
          <div className={`absolute ${isVisible ? 'top-[88px]' : 'top-[71px]'} left-1/2 transform -translate-x-1/2 z-[9999]`}>
            <div className="bg-red-600 text-white px-4 py-2 rounded-md shadow-lg font-mono text-sm font-bold animate-pulse">
              {t("debug.debugModeActive")}
            </div>
          </div>
        )}

        {/* LeftPanel */}
        <div className={`absolute ${isVisible ? 'top-6' : 'top-3'
          } left-3 w-40 bg-toolbkg rounded-md border border-gray-300 shadow-md`}>
          <LeftPanel />
        </div>

        {/* Property panel + next-step CTA stacked on the right */}
        <div
          className={`absolute ${isVisible ? "top-6" : "top-3"} right-3 bottom-3 z-30 flex w-[226px] min-w-0 flex-col gap-2`}
        >
          <div
            className={`min-h-0 min-w-0 select-none overflow-x-hidden overflow-y-auto overscroll-contain rounded-md border border-gray-300 bg-[#FCFCFC] shadow-md ${
              editorStore.minimized ? "" : "flex-1"
            }`}
          >
            <PropertyPanel />
          </div>
          {!editorStore.minimized && <AddToPresentationCta className="w-full shrink-0" />}
        </div>
        <ZoomControls
          className={`absolute bottom-3 ${editorStore.minimized ? "right-3" : "right-[246px]"}`}
        />
        <HistoryControls className="absolute bottom-3 left-3" />
        <CameraViewControls className="absolute bottom-3 left-1/2 -translate-x-1/2" />
        {editorStore.downloadProjectVisible && (
          <DownloadProject
            position={editorStore.downloadProjectPosition}
            onClose={editorStore.closeDownloadProjectPopup}
            dropdownRef={downloadProjectRef}
          />
        )}
        {editorStore.addToPresentationVisible && (
          <AddToPresentation
            position={editorStore.addToPresentationPosition}
            onClose={editorStore.closeAddToPresentationPopup}
            dropdownRef={addToPresentationRef}
          />
        )}
        {editorStore.render3DViewModalVisible && (
          <Render3DViewModal
            onClose={editorStore.closeRender3DViewModal}
            dropdownRef={render3DViewModalRef}
          />
        )}
        <PaywallModal />
      </div>

      {editorStore.roomPickerVisible && (
        <RoomPickerWizard
          onComplete={() => editorStore.closeRoomPicker()}
          onCancel={() => {
            clearRoomPickerFlag();
            editorStore.closeRoomPicker();
          }}
        />
      )}
    </div>
  );
});
export default Main;
